<template>
<e-chart ref="chart" />
</template>

<script>
import {AXIS_LABEL, AXIS_LINE_STYLE, TEXT_FONT_FAMILY} from './FactorCharts/chartStyle'

export default {
  name: 'AirQualityPredictionChart',
  props: {
    index: {
      required: true,
      type: Number
    },
    data: {
      required: true,
      type: Array
    },
    day: {
      required: true,
      type: String
    },
    clientHeight: {
      required: true,
      type: Number
    },
    clientWidth: {
      required: true,
      type: Number
    }
  },
  computed: {
    seriesMap () {
      return {
        aqi: {
          name: 'AQI',
          type: 'bar',
          colorBy: 'data',
          xAxisIndex: 0,
          yAxisIndex: 0,
          markPoint: {
            data: [
              { type: 'max', name: 'Max' },
              { type: 'min', name: 'Min' }
            ],
            symbolSize: 0,
            label: {
              fontSize: 10
            }
          },
          data: []
        },
        so2: {
          name: 'SO2',
          type: 'bar',
          xAxisIndex: 1,
          yAxisIndex: 1,
          data: []
        },
        no2: {
          name: 'NO2',
          type: 'bar',
          xAxisIndex: 2,
          yAxisIndex: 2,
          data: []
        },
        co: {
          name: 'CO',
          type: 'bar',
          xAxisIndex: 3,
          yAxisIndex: 3,
          data: []
        },
        o3: {
          name: 'O3',
          type: 'bar',
          xAxisIndex: 4,
          yAxisIndex: 4,
          data: []
        },
        pm25: {
          name: 'PM2.5',
          type: 'bar',
          xAxisIndex: 5,
          yAxisIndex: 5,
          data: []
        },
        pm10: {
          name: 'PM10',
          type: 'bar',
          xAxisIndex: 6,
          yAxisIndex: 6,
          data: []
        }
      }
    }
  },
  watch: {
    clientHeight: {
      immediate: true,
      handler (data) {
        if (data > 0) {
          setTimeout(() => {
            this.renderChart()
          }, 100)
        }
      }
    },
    clientWidth (val) {
      setTimeout(() => {
        this.$refs.chart.resize()
      }, 1)
    }
  },
  methods: {
    buildGrid () {
      let {seriesMap, index, clientHeight} = this
      let height = Math.floor(clientHeight / 7) - 10
      return Object.values(seriesMap).map((el, i) => {
        return {
          right: index === 2 ? 50 : 10,
          left: index === 0 ? 40 : 10,
          top: i === 0 ? 25 : height * i + 30,
          height: height
        }
      })
    },
    buildxAxis () {
      let {seriesMap} = this
      return Object.values(seriesMap).map((el, i) => {
        return {
          type: 'category',
          gridIndex: i,
          axisLabel: {
            ...AXIS_LABEL,
            fontFamily: TEXT_FONT_FAMILY,
            show: i + 1 === Object.values(seriesMap).length,
            formatter: function (param) {
              return Number(param) + 1
            }
          },
          axisLine: {
            show: true,
            lineStyle: AXIS_LINE_STYLE
          },
          axisTick: {
            show: i + 1 === Object.values(seriesMap).length,
            alignWithLabel: true
          }
        }
      })
    },
    buildyAxis () {
      let {seriesMap, index} = this
      return Object.values(seriesMap).map((el, i) => {
        let temp = {}
        if (index === 0) {
          temp = {
            name: el.name,
            nameLocation: 'middle'
          }
        }

        if (index === 2) {
          let name
          if (el.name === 'AQI') {
            name = ''
          } else {
            name = el.name === 'CO' ? 'mg/m3' : 'μg/m3'
          }
          temp = {
            name: name,
            nameLocation: 'middle',
            position: 'right'
          }
        }

        return {
          id: i,
          ...temp,
          nameTextStyle: {
            fontSize: 10
          },
          type: 'value',
          gridIndex: i,
          scale: true,
          axisLine: {
            show: false
          },
          splitLine: {
            show: false
          },
          axisLabel: {
            show: false
          }
        }
      })
    },
    buildSeries (data) {
      let seriesMap = JSON.parse(JSON.stringify(this.seriesMap))

      let temp = data.reduce((map, el) => {
        if (!map.aqi) {
          map = {
            ...seriesMap
          }
        }

        map.aqi.data.push(el.aqiData)
        map.so2.data.push(el.so2Data)
        map.no2.data.push(el.no2Data)
        map.co.data.push(el.coData)
        map.o3.data.push(el.o3Data)
        map.pm25.data.push(el.pm25Data)
        map.pm10.data.push(el.pm10Data)

        return map
      }, {})

      return Object.values(temp)
    },
    renderChart (isReal) {
      let {data, day} = this
      let chartOpt = {
        title: {
          text: new Date(day).format('YYYY年MM月DD日'),
          left: 'center',
          textStyle: {
            fontFamily: TEXT_FONT_FAMILY
          }
        },
        tooltip: {
          trigger: 'axis',
          appendToBody: true,
          formatter: function (param) {
            let dataTime = new Date(param[0].data.dataTime).getTime()
            return `<div>
              <div>${new Date(dataTime + 3600000).format('YYYY/MM/DD HH:mm:ss')}</div>
              <div>
                ${param.map(el => {
    let unit
    if (el.seriesName === 'AQI') {
      unit = ''
    } else {
      unit = el.seriesName === 'CO' ? 'mg/m3' : 'μg/m3'
    }
    return `<p style="margin: 0;display: flex;justify-content: space-between;">
                    <span style="margin-right: 15px;">${el.marker}${el.seriesName}</span>
                    <span>${el.data ? (Number.isFinite(el.data.value) ? el.data.value : '--') : '--'} ${unit}</span>
                  </p>`
  }).join('')}
              </div>
            </div>`
          }
        },
        axisPointer: {
          link: [{
            xAxisIndex: 'all'
          }],
          label: {
            backgroundColor: '#777'
          }
        },
        grid: this.buildGrid(),
        xAxis: this.buildxAxis(),
        yAxis: this.buildyAxis(),
        series: this.buildSeries(data)
      }

      this.$refs.chart.setOpt(chartOpt)
      this.$refs.chart.resize()
    }
  }
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/Data/AirQualityPredictionChart.vue